import type { components } from "../../api/schema";

type WorldlineNode = components["schemas"]["WorldlineNode"];

/**
 * APIから取得したフラット配列をツリー構造へ変換した後の型。
 * WorldlineNode の全フィールドを引き継ぎ、children を持つ。
 */
export type WorldlineTreeNode = WorldlineNode & {
  children: WorldlineTreeNode[];
};

/**
 * フラット配列を parentId による親子関係に従ってツリー構造に変換する。
 *
 * - parentId が null のノードがルートになる
 * - 存在しない parentId を持つ孤立ノードはルート扱いにする（安全処理）
 * - 兄弟ノードは createdAt 昇順にソートする
 *
 * @param nodes API から取得した WorldlineNode のフラット配列
 * @returns ルートノードの配列（各ノードが children を持つツリー構造）
 */
export function buildWorldlineTree(nodes: WorldlineNode[]): WorldlineTreeNode[] {
  // id → WorldlineTreeNode のマップを構築する
  const nodeMap = new Map<string, WorldlineTreeNode>();
  for (const node of nodes) {
    nodeMap.set(node.id, { ...node, children: [] });
  }

  const roots: WorldlineTreeNode[] = [];

  for (const node of nodes) {
    const treeNode = nodeMap.get(node.id);
    // nodeMap から必ず取れるため undefined にはならないが型安全のため確認する
    if (treeNode === undefined) continue;

    if (node.parentId === null) {
      // ルートノード
      roots.push(treeNode);
    } else {
      const parent = nodeMap.get(node.parentId);
      if (parent !== undefined) {
        // 親が存在する場合は子として追加する
        parent.children.push(treeNode);
      } else {
        // 親が存在しない孤立ノードはルート扱いにする
        roots.push(treeNode);
      }
    }
  }

  // 兄弟ノードを createdAt 昇順でソートする（再帰的に全レベルに適用）
  sortSiblings(roots);

  return roots;
}

/**
 * ツリーの全レベルで兄弟ノードを createdAt 昇順にソートする。
 */
function sortSiblings(siblings: WorldlineTreeNode[]): void {
  siblings.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const node of siblings) {
    if (node.children.length > 0) {
      sortSiblings(node.children);
    }
  }
}
